/**
 * VRGrabbableComponent - marks an actor as grabbable and defines grab behavior/options.
 */

import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';

import { VRBaseComponent } from './VRBaseComponent.js';

import type { SceneComponentOptions } from '../../components/SceneComponent.js';
import type { XRAxis, XRButton } from '../VRControllerManager.js';



export interface VRGrabbableComponentOptions extends SceneComponentOptions {
  vrAttachmentInputType?: 'grip' | 'select';
  offsetPosition?: THREE.Vector3;
  offsetRotation?: THREE.Euler;
  offsetScale?: THREE.Vector3;
  hideController?: boolean;
  attachmentMode?: 'hand' | 'beam';
  useBeamPickupDistance?: boolean;
  beamPickupDistance?: number;
}

@EngineClass('VRGrabbableComponent')
export class VRGrabbableComponent extends VRBaseComponent {


  @property()
  public vrAttachmentInputType: 'grip' | 'select' = 'grip';

  @property()
  public hideController: boolean = false;

  @property()
  public attachmentMode: 'hand' | 'beam' = 'hand';

  @property()
  public offsetPosition: THREE.Vector3 = new THREE.Vector3(0, 0, 0);

  @property()
  public offsetRotation: THREE.Euler = new THREE.Euler(0, 0, 0);

  @property()
  public offsetScale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);

  @property()
  public useBeamPickupDistance: boolean = false;

  @property()
  public beamPickupDistance: number = 0.5;


  private grabEnabled: boolean = true;

  public override initialize(options?: VRGrabbableComponentOptions): void {
    super.initialize(options);
    this.vrAttachmentInputType = options?.vrAttachmentInputType ?? this.vrAttachmentInputType;
    this.hideController = options?.hideController ?? this.hideController;
    this.attachmentMode = options?.attachmentMode ?? this.attachmentMode;
    this.offsetPosition = options?.offsetPosition ?? this.offsetPosition;
    this.offsetRotation = options?.offsetRotation ?? this.offsetRotation;
    this.offsetScale = options?.offsetScale ?? this.offsetScale;
    this.useBeamPickupDistance = options?.useBeamPickupDistance ?? this.useBeamPickupDistance;
    this.beamPickupDistance = options?.beamPickupDistance ?? this.beamPickupDistance;
  }



  public getVRAttachmentInputType(): 'grip' | 'select' {
    return this.vrAttachmentInputType;
  }

  public getVRAttachmentOptions(): VRGrabbableComponentOptions {
    return {
      vrAttachmentInputType: this.vrAttachmentInputType,
      hideController: this.hideController,
      attachmentMode: this.attachmentMode,
      offsetPosition: this.offsetPosition,
      offsetRotation: this.offsetRotation,
      offsetScale: this.offsetScale,
      useBeamPickupDistance: this.useBeamPickupDistance,
      beamPickupDistance: this.beamPickupDistance,
    };
  }

  public setGrabEnabled(enabled: boolean): void {
    this.grabEnabled = enabled;
  }

  public canGrab(): boolean {
    return this.grabEnabled;
  }

  public readonly onGrabStart: Delegate<[hand: 'left' | 'right'], void> = new Delegate();
  public readonly onGrabEnd: Delegate<[hand: 'left' | 'right', dropVelocity?: THREE.Vector3], void> = new Delegate();
  public readonly onVRInput: Delegate<[hand: 'left' | 'right', control: XRButton | XRAxis, pressed: boolean, value: number], boolean> = new Delegate();
}


